import React, { useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, Modal, Typography } from 'antd';

import { affectedSentences } from '../../utils/relatedLists';

import { revertChange } from '../../services/history';
import { actionLabel, formatChangeValue, formatEntryValue, hasTagArrayChanges, historyTagEntries, isEntryChange, isEntryMove, isTagArrayChange, isTagDeletionEntry } from '../../utils/history';
import { tagsById } from '../../utils/tags';
import useCollectionTags from '../../hooks/useCollectionTags';
import ChangeTable, { isHiddenValuesChange, isProseChange } from './ChangeTable';
import { HistoryTagRow } from './HistoryTagBadges';
import MaskedValue from '../common/MaskedValue';
import { cardChangeRevertExplanation, cardChangeRevertNotices, isCardChangeEntry } from './cardChangeRevert';

const { Text, Paragraph } = Typography;

/**
 * The revert flow for one history entry. What it does depends on the entry:
 *
 *   update  — undo the recorded changes. Every field is selected by default;
 *             the table lets the user keep some. If a field has changed
 *             since, the API answers 409 with the conflicts, shown here with
 *             a red "Revert anyway" that overwrites.
 *   delete  — restore the record under its old id (with the relationships
 *             the delete took with it, where they still fit, and its
 *             attachments while their bytes are kept — 30 days).
 *   attachment.delete — the ✕ on a file: a soft delete, so an update row,
 *             but there is nothing to pick — the file simply goes back.
 *   create  — delete the record again. If it has been edited since, the API
 *             answers 409 modified_since and the red door is "Delete anyway".
 *   item.changeCard — a card change (KPR-704): reverted whole, no field
 *             table. The item goes back to the card it came from with every
 *             value the change took away; what was entered since stays in
 *             History, and the notice afterwards says which, and what could
 *             not come back. A refusal is the API's sentence, never forced.
 *   tag.delete (a rebuilt collection tag, v: 2) — a soft delete, so an update
 *             row, that reads as ONE act: Restore. The tag comes back and goes
 *             back on the items its delete took it off (keepr-api POST
 *             /api/tags/{id}/restore); a refusal (its parent deleted, the name
 *             taken, deleted again since) is the API's sentence, never forced.
 *
 * Every revert is a new write that the log records as a revert; nothing here
 * edits the log itself.
 *
 * `restore` (Recently deleted, delete audit F5): the entry is a DELETE —
 * hard (op delete) or soft (a collection, card or element set whose status
 * moved: an update row) — and the modal reads as one act, "Restore", with no
 * field table: the whole delete is undone, never a subset of it.
 */
const RESTORE_WHAT = {
	collection: 'The collection comes back with its items, cards and sharing, and its stored exports switch back on. Collection links its deletion ended stay ended, and rules an account deletion switched off stay off until a manager turns them on.',
	card: 'The card comes back to its collection, with its elements and the rules they drive.',
	elementSet: 'The element set comes back under the same key.',
	// A rebuilt collection tag's delete (v: 2 — keepr-api POST /api/tags/{id}/restore).
	tag: 'The tag comes back where it was, and goes back on the items it was taken off, where they can still carry it.',
};

/**
 * What a hidden-values revert left out, as sentences (keepr-api
 * `restored.skipped`): items deleted or moved since, and entries removed from
 * a repeating group since (KPR-751). Empty when it left nothing out.
 */
export function hiddenValuesSkippedSentences(skipped) {
	const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
	return (Array.isArray(skipped) ? skipped : []).map((s) => {
		const n = Number(s?.count) || 0;
		if (!n) return null;
		if (s.type === 'entry' && s.reason === 'entry gone') return `Skipped ${plural(n, 'entry', 'entries')} removed from “${s.element}” since.`;
		if (s.type === 'item' && s.reason === 'item gone') return `Skipped ${plural(n, 'item', 'items')} deleted since.`;
		if (s.type === 'item' && s.reason === 'item moved') return `Skipped ${plural(n, 'item', 'items')} moved to another collection since.`;
		return null;
	}).filter(Boolean);
}

/**
 * The restore's note about values left out (KPR-1136): "Restored without
 * "ref": it became an item lookup after the item was deleted."
 */
export function lookupsLeftOutSentence(names) {
	const list = (names || []).map(name => `"${name}"`);
	if (!list.length) return '';
	const joined = list.length === 1 ? list[0] : `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
	return `Restored without ${joined}: ${list.length === 1 ? 'it' : 'they'} became ${list.length === 1 ? 'an item lookup' : 'item lookups'} after the item was deleted, and what ${list.length === 1 ? 'it' : 'they'} held then was not a link.`;
}

export default function RevertChangeModal({ open, entry, onClose, onReverted, restore = false }) {
	const { message, notification } = App.useApp();
	const [selected, setSelected] = useState([]);
	const [busy, setBusy] = useState(false);
	const [conflict, setConflict] = useState(null); // { code, conflicts?, later?, message }

	// An item's tag applications read as the tags' badges, as in the feed —
	// never links here: the dialog is a decision in progress (ruled b54).
	const { tags } = useCollectionTags(entry?.scope?.collection_id, { enabled: Boolean(open) && hasTagArrayChanges(entry) });
	const tagIndex = useMemo(() => tagsById(tags), [tags]);
	const changes = useMemo(() => (Array.isArray(entry?.changes) ? entry.changes.filter(c => !c.redacted) : []), [entry]);
	// A rebuilt tag's deletion reads as a restore: its revert IS the tag's
	// restore, whole — never a subset of fields.
	const isTagRestore = isTagDeletionEntry(entry);
	// A conversion entry is prose about a backfill, not a value: it is shown
	// but never a revert target (the API answers 400 not_revertable for a
	// partial revert that names it; a full revert simply ignores it).
	const revertable = useMemo(() => changes.filter(c => !isProseChange(c)), [changes]);
	// A removed attachment is an update row (its status moved) that reads as a
	// restore: no field table, one "Restore" button.
	const isFileRestore = entry?.action === 'attachment.delete';
	// A purge of hidden values (`card.purgeValues`, or the `card.restoreValues`
	// that undid one) IS revertable, but only as a whole row: the revert puts
	// back — or purges again — every value the row names. No field picker;
	// the request names no paths.
	const hiddenValues = useMemo(() => changes.find(isHiddenValuesChange)?.hiddenValues || null, [changes]);
	const wholeRow = Boolean(hiddenValues);
	// A card change goes back whole (KPR-704): no field picker, no paths.
	const isCardChange = isCardChangeEntry(entry);
	const isUpdate = entry?.op === 'update' && !isFileRestore && !wholeRow && !restore && !isTagRestore && !isCardChange;

	useEffect(() => {
		if (!open) return;
		setSelected(revertable.map(c => c.path));
		setConflict(null);
		setBusy(false);
	}, [open, revertable]);

	const partial = isUpdate && selected.length > 0 && selected.length < revertable.length;

	const submit = async ({ force = false } = {}) => {
		if (!entry) return;
		setBusy(true);
		try {
			const result = await revertChange(entry._id, {
				force,
				paths: partial ? selected : undefined,
			});
			message.success(result?.change?.summary || 'Change reverted.');
			// An item restore whose attachments' contents are gone (past the
			// 30 days, or deleted before deleted files were kept at all): the
			// record is back, those files are not — said, never silent.
			const lost = result?.restored?.attachmentsLost || 0;
			if (lost) message.warning(`${lost} attachment${lost === 1 ? '' : 's'} could not be restored: ${lost === 1 ? 'its contents are' : 'their contents are'} gone.`);
			// A purge's revert that found items or entries gone since: said, never silent (KPR-751).
			const skippedLines = wholeRow ? hiddenValuesSkippedSentences(result?.restored?.skipped) : [];
			if (skippedLines.length) message.warning(skippedLines.join(' '));
			// A restored item's values under elements that became item lookups
			// after it was deleted come back empty (KPR-1136): what they held
			// then was not a link anyone chose.
			const lookupsLeftOut = Array.isArray(result?.restored?.lookupsLeftOut) ? result.restored.lookupsLeftOut : [];
			if (lookupsLeftOut.length) message.warning(lookupsLeftOutSentence(lookupsLeftOut));
			// A card revert's related lists: the ones it left out, and the ones
			// the restored card leaves finding nothing — said in the card
			// editor's words, as text (relatedListsAffected, keepr-api KPR-747).
			const leftOut = affectedSentences((result?.relatedListsAffected || []).filter(entry => entry?.leftOut));
			const finding = affectedSentences((result?.relatedListsAffected || []).filter(entry => !entry?.leftOut));
			let notice = null;
			// A card change reverted: what was entered since (kept in History),
			// what could not come back, a comment left because it was edited —
			// said until it is read, never silent (KPR-704).
			const cardChangeLines = isCardChange ? cardChangeRevertNotices(result?.cardChange) : [];
			if (cardChangeLines.length) {
				const closers = [];
				notification.info({
					key: 'revert-card-change',
					message: 'Card change reverted',
					description: <ul className="revert-notice__lines">{cardChangeLines.map(line => <li key={line}>{line}</li>)}</ul>,
					duration: 0,
					onClose: () => closers.forEach(fn => fn()),
				});
				notice = { afterClose: fn => closers.push(fn) };
			}
			if (leftOut.length || finding.length) {
				const closers = [];
				notification.warning({
					key: 'revert-related-lists',
					message: leftOut.length ? 'Related lists left out of the revert' : 'Related lists affected',
					description: (
						<>
							{leftOut.length ? <ul className="revert-notice__lines">{leftOut.map(line => <li key={line}>{line}</li>)}</ul> : null}
							{leftOut.length && finding.length ? <Paragraph className="revert-notice__also">Also affected</Paragraph> : null}
							{finding.length ? <ul className="revert-notice__lines">{finding.map(line => <li key={line}>{line}</li>)}</ul> : null}
						</>
					),
					duration: 0,
					onClose: () => closers.forEach(fn => fn()),
				});
				// The caller may hold what it does next (a reload) until this is read.
				notice = { afterClose: fn => closers.push(fn) };
			}
			if (typeof onReverted === 'function') onReverted(result, notice);
			onClose();
		} catch (error) {
			const status = error?.response?.status;
			const data = error?.response?.data || {};
			if (status === 409) {
				setConflict({ code: data.code, conflicts: data.conflicts || [], later: data.later, message: data.message });
			} else if (status === 403) {
				// An unverified email is a 403 the person can fix — say that one
				// in the API's words rather than as a missing permission.
				message.error(data.code === 'email_unverified'
					? (data.message || 'Verify your email address before reverting changes.')
					: 'You are not allowed to revert this change.');
			} else if (status === 400 || status === 428) {
				message.error(data.message || 'This change cannot be reverted.');
			} else {
				message.error('Reverting failed. Nothing was changed.');
			}
		} finally {
			setBusy(false);
		}
	};

	let title = entry ? `Revert: ${actionLabel(entry.action, entry).toLowerCase()} ${entry.target?.label ? `“${entry.target.label}”` : ''}`.trim() : 'Revert';
	if (entry && (restore || isTagRestore)) title = `Restore ${entry.target?.label ? `“${entry.target.label}”` : 'this'}?`;
	// A tag's restore answers only its newest deletion, and a card change goes
	// back whole or not at all: nothing overrides either.
	const forceable = conflict && !isTagRestore && !isCardChange && (conflict.code === 'conflicts' || conflict.code === 'modified_since');
	const primaryLabel = wholeRow ? 'Revert' : restore || isTagRestore || entry?.op === 'delete' || isFileRestore ? 'Restore' : entry?.op === 'create' ? 'Delete again' : 'Revert';

	return (
		<Modal
			open={open}
			onCancel={onClose}
			title={title}
			destroyOnClose
			footer={[
				<Button key="cancel" onClick={onClose} disabled={busy}>Cancel</Button>,
				forceable ? (
					<Button key="force" danger type="primary" loading={busy} onClick={() => submit({ force: true })}>
						{!wholeRow && entry?.op === 'create' ? 'Delete anyway' : restore ? 'Restore anyway' : 'Revert anyway'}
					</Button>
				) : (
					<Button
						key="ok"
						type="primary"
						loading={busy}
						disabled={isUpdate && selected.length === 0}
						onClick={() => submit()}
					>
						{primaryLabel}
					</Button>
				),
			]}
		>
			{entry?.summary ? <Paragraph type="secondary" className="revert-modal__summary">{entry.summary}</Paragraph> : null}

			{isUpdate ? (
				<>
					<Paragraph>
						These fields go back to the values they had before this change. Untick any you want to keep as they are now.
					</Paragraph>
					<ChangeTable
						changes={changes}
						selectable
						selectedPaths={selected}
						onSelectionChange={keys => { setSelected(keys); setConflict(null); }}
						tagIndex={tagIndex}
						linkTags={false}
					/>
				</>
			) : null}

			{wholeRow ? (
				<>
					<Paragraph>
						{hiddenValues.op === 'restore'
							? 'Reverting purges these values again: every value this change put back is removed from its item.'
							: 'Reverting puts back every value this purge removed, on every item it removed one from. They stay hidden until the card has an element with the same name again.'}
					</Paragraph>
					<ChangeTable changes={changes} tagIndex={tagIndex} linkTags={false} />
				</>
			) : null}

			{isCardChange ? (
				<ul className="revert-modal__card-change">
					{cardChangeRevertExplanation(entry).map(line => <li key={line}>{line}</li>)}
				</ul>
			) : null}

			{!wholeRow && entry?.op === 'delete' ? (
				<Paragraph>
					The record comes back under its original id, with the relationships its deletion removed where they still fit, and the attachments it removed — unless that was more than 30 days ago.
				</Paragraph>
			) : null}

			{(restore || isTagRestore) && entry?.op !== 'delete' && RESTORE_WHAT[entry?.target?.type] ? (
				<Paragraph>{RESTORE_WHAT[entry.target.type]}</Paragraph>
			) : null}

			{isFileRestore ? (
				<Paragraph>
					The file goes back on its item. If the item has been deleted since, restore the item first.
				</Paragraph>
			) : null}

			{!wholeRow && entry?.op === 'create' ? (
				<Paragraph>
					The record this change created will be deleted. If it has been edited since, you will be asked to confirm.
				</Paragraph>
			) : null}

			{conflict ? (
				<Alert
					className="revert-modal__conflict"
					type="warning"
					showIcon
					message={isTagRestore ? (conflict.message || 'This tag cannot be restored as things stand.') : conflict.code === 'modified_since'
						? `This record has ${conflict.later ?? 'later'} change${conflict.later === 1 ? '' : 's'} since it was created.`
						: conflict.code === 'conflicts'
							? 'Some values have changed since this change was recorded.'
							: (conflict.message || 'This change cannot be reverted as things stand.')}
					description={conflict.code === 'conflicts' && conflict.conflicts.length ? (
						<ul className="revert-modal__conflicts">
							{conflict.conflicts.map(c => (
								<li key={c.path}>
									<Text code>{labelFor(changes, c.path)}</Text>
									{isTagArrayChange(c) ? (
										<>
											{' '}is now <ConflictTags ids={c.actual} tagIndex={tagIndex} />, not <ConflictTags ids={c.expected} tagIndex={tagIndex} />
										</>
									) : isEntryMove(changeAt(changes, c.path)) ? (
										': the order has changed since'
									) : isWholeEntryChange(changeAt(changes, c.path)) ? (
										<EntryConflict conflict={c} masked={changeAt(changes, c.path)?.masked === true} label={labelFor(changes, c.path)} />
									) : changeAt(changes, c.path)?.masked === true ? (
										// A masked element (KPR-1348): both values behind the mask, with Show.
										<>
											{' '}is now <MaskedValue value={c.actual} text={formatChangeValue(c.actual)} label={`${labelFor(changes, c.path)} now`} />, not <MaskedValue value={c.expected} text={formatChangeValue(c.expected)} label={`${labelFor(changes, c.path)} expected`} />
										</>
									) : (
										<>
											{' '}is now <Text strong>{formatChangeValue(c.actual)}</Text>, not <Text>{formatChangeValue(c.expected)}</Text>
										</>
									)}
									{c.reason === 'redacted' ? ' (value withheld)' : ''}
									{c.reason === 'card-lookup' ? ' (an item lookup now, so a value purged from it stays purged)' : ''}
								</li>
							))}
						</ul>
					) : (forceable ? 'Revert anyway to overwrite what is there now.' : null)}
				/>
			) : null}
		</Modal>
	);
}

/** An entry added or removed (KPR-678), not one of its columns or a heading's title. */
function isWholeEntryChange(change) {
	return isEntryChange(change) && !change.entry.column && !change.entry.section;
}

/** A conflict on a whole entry: gone since, back since, or changed since. */
function EntryConflict({ conflict, masked = false, label = 'Entry' }) {
	const gone = conflict.actual === undefined || conflict.actual === null;
	const absent = conflict.expected === undefined || conflict.expected === null;
	if (gone) return <> has been removed since</>;
	// An entry holding a MASKED column's value (keepr-api `masked`, KPR-1353):
	// both readings behind the mask, each with its own Show.
	const shown = (value, strong, side) => {
		const text = formatEntryValue(value);
		if (!masked || text === '—') return <Text strong={strong}>{text}</Text>;
		return <MaskedValue value={text} text={text} label={`${label} ${side}`} />;
	};
	if (absent) return <> is back already: {shown(conflict.actual, true, 'now')}</>;
	return <> is now {shown(conflict.actual, true, 'now')}, not {shown(conflict.expected, false, 'expected')}</>;
}

function changeAt(changes, path) {
	return (changes || []).find(c => c.path === path) || null;
}

function labelFor(changes, path) {
	return changeAt(changes, path)?.label || path;
}

/** A conflict's tag list (ids only): the tags' badges, never links; none reads "no tags". */
function ConflictTags({ ids, tagIndex }) {
	const entries = historyTagEntries(ids, null, tagIndex);
	if (!entries.length) return <Text strong>no tags</Text>;
	return <span className="revert-modal__tags"><HistoryTagRow entries={entries} linked={false} /></span>;
}
